import { Film, Sparkles, Zap, Check } from "lucide-react";
import {
  VIDEO_ANIMATION_TYPES,
  DURATION_OPTIONS,
  SOCIAL_MEDIA_PACK,
  getShotCountText,
  getAnimationTier,
} from "../../pages/calculators/videoAnimationPricing";

/**
 * VideoAnimationPicker
 * Two-card selector: AI Motion + Cinematic Film (single exclusive selection).
 * Optional Social Media Pack toggle.
 *
 * Props:
 *   selectedType: 'ai' | 'cinematic' | null
 *   selectedDuration: 30 | 60 | 90 | null
 *   socialMediaPack: boolean
 *   onSelect(type, duration)  — selects or deselects (pass null,null to clear)
 *   onToggleSocialPack(checked)
 *   compact?: boolean          — tighter spacing for sidebars
 *   testIdPrefix?: string      — prefix for data-testids (e.g. "viz" / "combined")
 */
export default function VideoAnimationPicker({
  selectedType,
  selectedDuration,
  socialMediaPack,
  onSelect,
  onToggleSocialPack,
  compact = false,
  testIdPrefix = "video-anim",
  rateResolver = null,
}) {
  const active = selectedType && selectedDuration;

  return (
    <div className={compact ? "space-y-4" : "space-y-6"}>
      <div
        className={`grid gap-${compact ? "3" : "4"} ${
          compact ? "grid-cols-1" : "grid-cols-1 lg:grid-cols-2"
        }`}
      >
        {Object.values(VIDEO_ANIMATION_TYPES).map((typeDef) => (
          <TypeCard
            key={typeDef.key}
            typeDef={typeDef}
            selectedType={selectedType}
            selectedDuration={selectedDuration}
            onSelect={onSelect}
            compact={compact}
            testIdPrefix={testIdPrefix}
            rateResolver={rateResolver}
          />
        ))}
      </div>

      {/* Shot count helper text — reflects active selection */}
      {active && (
        <div
          className={`flex items-center gap-2 text-xs text-[#C9C6C1] bg-white/[0.03] border border-white/[0.06] ${
            compact ? "px-3 py-2" : "px-4 py-2.5"
          } rounded-lg`}
          data-testid={`${testIdPrefix}-shot-count`}
        >
          <Film className="w-3.5 h-3.5 text-[#8A8783]" />
          <span>{getShotCountText(selectedType, selectedDuration)}</span>
        </div>
      )}

      {/* Social Media Pack add-on — only when a video is selected */}
      {active && (
        <SocialMediaPackToggle
          checked={!!socialMediaPack}
          onToggle={onToggleSocialPack}
          compact={compact}
          testIdPrefix={testIdPrefix}
        />
      )}
    </div>
  );
}

function TypeCard({
  typeDef,
  selectedType,
  selectedDuration,
  onSelect,
  compact,
  testIdPrefix,
  rateResolver,
}) {
  const isThisTypeActive = selectedType === typeDef.key;
  const Icon = typeDef.key === "ai" ? Zap : Sparkles;

  return (
    <div
      className={`
        relative rounded-2xl border transition-all duration-200
        ${
          isThisTypeActive
            ? "border-white/20 bg-[#121212]"
            : "border-white/[0.08] bg-[#000000] hover:border-white/[0.14]"
        }
        ${compact ? "p-4" : "p-5"}
      `}
      data-testid={`${testIdPrefix}-type-card-${typeDef.key}`}
    >
      {/* Header */}
      <div className="flex items-start gap-3 mb-4">
        <div
          className={`flex items-center justify-center rounded-full ${
            compact ? "w-8 h-8" : "w-9 h-9"
          } ${
            isThisTypeActive
              ? "bg-white/10 text-[#F4F2EE]"
              : "bg-white/[0.04] text-[#8A8783]"
          }`}
        >
          <Icon className={compact ? "w-3.5 h-3.5" : "w-4 h-4"} />
        </div>
        <div className="flex-1 min-w-0">
          <div
            className={`font-semibold text-[#F4F2EE] ${
              compact ? "text-sm" : "text-[15px]"
            }`}
          >
            {typeDef.label}
          </div>
          <div className="text-[11px] text-[#8A8783] mt-0.5">
            {typeDef.tagline}
          </div>
        </div>
      </div>

      <p
        className={`text-[12px] leading-relaxed text-[#8A8783] ${
          compact ? "mb-4" : "mb-5"
        }`}
      >
        {typeDef.subtitle}
      </p>

      {/* Duration pills */}
      <div className="grid grid-cols-3 gap-2">
        {DURATION_OPTIONS.map((duration) => {
          const tier = getAnimationTier(typeDef.key, duration);
          // Prefer admin-editable rate from Pricing Admin when a resolver is provided;
          // fall back to the hardcoded tier.price (legacy / first paint).
          const displayPrice = rateResolver
            ? rateResolver(typeDef.key, duration)
            : tier.price;
          const isSelected =
            isThisTypeActive && selectedDuration === duration;
          return (
            <button
              key={duration}
              onClick={() => {
                if (isSelected) {
                  // Clicking the already-selected pill clears selection
                  onSelect(null, null);
                } else {
                  onSelect(typeDef.key, duration);
                }
              }}
              className={`
                group relative rounded-xl border text-left transition-all duration-200
                ${compact ? "px-3 py-2.5" : "px-3.5 py-3"}
                ${
                  isSelected
                    ? "border-[#F4F2EE] bg-white text-[#000000]"
                    : "border-white/[0.08] bg-transparent text-[#F4F2EE] hover:border-white/20 hover:bg-white/[0.04]"
                }
              `}
              data-testid={`${testIdPrefix}-${typeDef.key}-duration-${duration}`}
            >
              <div
                className={`${
                  compact ? "text-[11px]" : "text-[12px]"
                } font-medium tracking-wide opacity-70`}
              >
                {tier.label}
              </div>
              <div
                className={`${
                  compact ? "text-sm" : "text-[15px]"
                } font-semibold mt-0.5 tabular-nums`}
              >
                ${displayPrice.toLocaleString()}
              </div>
              {isSelected && (
                <div className="absolute top-2 right-2">
                  <Check className="w-3 h-3 text-[#000000]" />
                </div>
              )}
            </button>
          );
        })}
      </div>
    </div>
  );
}

function SocialMediaPackToggle({ checked, onToggle, compact, testIdPrefix }) {
  return (
    <button
      type="button"
      onClick={() => onToggle && onToggle(!checked)}
      className={`
        w-full flex items-center gap-4 rounded-2xl border text-left transition-all duration-200
        ${compact ? "px-4 py-3" : "px-5 py-4"}
        ${
          checked
            ? "border-white/20 bg-[#121212]"
            : "border-white/[0.08] bg-[#000000] hover:border-white/[0.14]"
        }
      `}
      data-testid={`${testIdPrefix}-social-pack-toggle`}
    >
      {/* Custom checkbox */}
      <div
        className={`flex-shrink-0 w-5 h-5 rounded-md border flex items-center justify-center transition-colors ${
          checked
            ? "bg-[#F4F2EE] border-[#F4F2EE]"
            : "bg-transparent border-white/20"
        }`}
      >
        {checked && <Check className="w-3 h-3 text-[#000000]" />}
      </div>
      <div className="flex-1 min-w-0">
        <div className="flex items-center justify-between gap-3">
          <span
            className={`font-medium text-[#F4F2EE] ${
              compact ? "text-sm" : "text-[14px]"
            }`}
          >
            {SOCIAL_MEDIA_PACK.label}
          </span>
          <span className="text-[#F4F2EE] font-semibold tabular-nums text-[14px]">
            +${SOCIAL_MEDIA_PACK.price.toLocaleString()}
          </span>
        </div>
        <p className="text-[11px] text-[#8A8783] mt-1 leading-relaxed">
          {SOCIAL_MEDIA_PACK.subtitle}
        </p>
      </div>
    </button>
  );
}
